import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { CreatePrescriptionDto, CreatePrescriptionItemDto } from '../../types/prescription.types';
import { DrugSelector } from './DrugSelector';
import { LabTestSelector } from './LabTestSelector';
import { RadiologyTestSelector } from './RadiologyTestSelector';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useQuery } from '@tanstack/react-query';
import { prescriptionsApi } from '../../api/prescriptions.api';
import { ErrorMessage } from '../common/ErrorMessage';
import { LabTest } from '../../types/api.types';
import { RadiologyTest } from '../../types/api.types';

interface PrescriptionBuilderProps {
  visitId: number;
  patientId?: number;
  onSubmit: (data: CreatePrescriptionDto) => Promise<number>;
  onPrint: (prescriptionId: number) => Promise<void> | void;
  isLoading?: boolean;
}

const PrescriptionBuilderComponent: React.FC<PrescriptionBuilderProps> = ({
  visitId,
  patientId,
  onSubmit,
  onPrint,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { isRTL, language } = useLanguageStore();
  const [items, setItems] = useState<CreatePrescriptionItemDto[]>([]);
  const [labTestIds, setLabTestIds] = useState<number[]>([]);
  const [radiologyTestIds, setRadiologyTestIds] = useState<number[]>([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load all data in a single request - automatically fetches when component mounts
  const { 
    data: initialData, 
    isLoading: isLoadingData, 
    isError: isErrorLoadingData,
    error: errorLoadingData,
    refetch: refetchInitialData 
  } = useQuery({
    queryKey: ['prescription-initial-data'],
    queryFn: () => prescriptionsApi.getInitialData(),
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache for 10 minutes
    retry: 2, // Retry failed requests 2 times
    retryDelay: 1000, // Wait 1 second between retries
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  const drugs = initialData?.drugs || [];
  const labTests = initialData?.labTests || [];
  const radiologyTests = initialData?.radiologyTests || [];
  
  // Refetch functions - refetch all data together
  const refetchLabTests = async () => {
    await refetchInitialData();
  };
  const refetchRadiologyTests = async () => {
    await refetchInitialData();
  };

  const handleAddDrug = (drug: CreatePrescriptionItemDto) => {
    setItems([...items, drug]);
  };

  const handleRemoveDrug = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleToggleLabTest = (id: number) => {
    if (labTestIds.includes(id)) {
      setLabTestIds(labTestIds.filter((testId) => testId !== id));
    } else {
      setLabTestIds([...labTestIds, id]);
    }
  };

  const handleToggleRadiologyTest = (id: number) => {
    if (radiologyTestIds.includes(id)) {
      setRadiologyTestIds(radiologyTestIds.filter((testId) => testId !== id));
    } else {
      setRadiologyTestIds([...radiologyTestIds, id]);
    }
  };

  const hasContent =
    items.length > 0 || labTestIds.length > 0 || radiologyTestIds.length > 0;

  const handleSubmit = async () => {
    if (!hasContent) {
      setError(t('prescriptions.addAtLeastOneItem'));
      return;
    }

    setError(null);

    try {
      const dto: CreatePrescriptionDto = {
        visitId,
        items,
        labTestIds,
        radiologyTestIds,
        notes: notes || undefined,
      };

      const prescriptionId = await onSubmit(dto);
      await onPrint(prescriptionId);
    } catch (error) {
      // Error is handled in the parent component
      console.error('Error creating prescription:', error);
    }
  };

  if (isLoadingData) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-3 text-muted">{t('prescriptions.loadingData', 'جاري تحميل البيانات...')}</p>
        </div>
      </div>
    );
  }

  if (isErrorLoadingData) {
    // Log error details for debugging
    console.error('Error loading prescription initial data:', errorLoadingData);
    
    // Extract error message
    let errorMessage = t('prescriptions.errorLoadingData', 'فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.');
    if (errorLoadingData instanceof Error) {
      const axiosError = errorLoadingData as any;
      if (axiosError?.response?.status === 401) {
        errorMessage = t('prescriptions.unauthorized', 'غير مصرح لك بالوصول. يرجى تسجيل الدخول مرة أخرى.');
      } else if (axiosError?.response?.status === 403) {
        errorMessage = t('prescriptions.forbidden', 'ليس لديك صلاحية للوصول إلى هذه البيانات.');
      } else if (axiosError?.response?.status >= 500) {
        errorMessage = t('prescriptions.serverError', 'خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.');
      } else if (axiosError?.message) {
        errorMessage = axiosError.message;
      }
    }
    
    return (
      <div className="d-flex flex-column justify-content-center align-items-center py-5" dir={isRTL ? 'rtl' : 'ltr'}>
        <ErrorMessage message={errorMessage} />
        <Button
          onClick={() => refetchInitialData()}
          variant="primary"
          className="mt-3"
        >
          {t('common.retry', 'إعادة المحاولة')}
        </Button>
        <p className="text-muted small mt-2 text-center" style={{ maxWidth: '500px' }}>
          {t('prescriptions.errorHelp', 'إذا استمرت المشكلة، تأكد من أن الخادم يعمل وأنك مسجل الدخول بشكل صحيح.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>

      <DrugSelector
        drugs={drugs || []}
        selectedDrugs={items}
        onAddDrug={handleAddDrug}
        onRemoveDrug={handleRemoveDrug}
        patientId={patientId}
      />

      <LabTestSelector
        labTests={labTests || []}
        selectedLabTestIds={labTestIds}
        onToggleLabTest={handleToggleLabTest}
        onLabTestAdded={async (_labTest: LabTest) => {
          await refetchLabTests();
        }}
      />

      <RadiologyTestSelector
        radiologyTests={radiologyTests || []}
        selectedRadiologyTestIds={radiologyTestIds}
        onToggleRadiologyTest={handleToggleRadiologyTest}
        onRadiologyTestAdded={async (_radiologyTest: RadiologyTest) => {
          await refetchRadiologyTests();
        }}
      />

      <div>
        <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem', direction: isRTL ? 'rtl' : 'ltr' }}>
          {t('prescriptions.notes')}
        </label>
        <textarea
          className="form-control"
          style={{
            minHeight: '100px',
            fontFamily: language === 'ar' ? 'var(--font-family-arabic)' : 'var(--font-family-base)',
            fontSize: '0.95rem',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            border: '2px solid #e5e7eb',
            transition: 'all 0.3s',
            textAlign: isRTL ? 'right' : 'left',
          }}
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('prescriptions.additionalNotes')}
          dir={isRTL ? 'rtl' : 'ltr'}
        />
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="d-flex gap-2">
        <Button 
          onClick={handleSubmit} 
          isLoading={isLoading} 
          disabled={!hasContent}
          variant="primary"
          fullWidth
        >
          {t('prescriptions.saveAndPrint')}
        </Button>
      </div>
    </div>
  );
};

export const PrescriptionBuilder = React.memo(PrescriptionBuilderComponent);

